import { useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useGame } from '../context/GameContext';
import { usePlayer } from '../context/PlayerContext';
import { chooseRevealPositions, chooseDrawSource, chooseSwapOrDiscard } from '../utils/botAI';
import { PHASES, TURN_PHASES } from '../utils/gameRules';

const BOT_DELAY = 1500;

function useBotPlayer() {
  const { state } = useGame();
  const { playerId, isHost } = usePlayer();
  const processingRef = useRef(false);
  const timeoutRef = useRef(null);

  const {
    gameState,
    players,
    playerOrder,
    currentTurn,
    discardPile,
    roundState,
    roomCode,
  } = state;

  const callFn = useCallback(
    async (name, data = {}) => {
      const fn = httpsCallable(functions, name);
      const result = await fn({ roomCode, ...data });
      return result.data;
    },
    [roomCode]
  );

  // Handle bot reveals during REVEALING phase
  useEffect(() => {
    if (!isHost || !roomCode) return;
    if (gameState.phase !== PHASES.REVEALING) return;

    const revealedPlayers = roundState?.revealedPlayers || [];
    const botsNeedingReveal = playerOrder.filter(
      (pid) => players[pid]?.isBot && !revealedPlayers.includes(pid)
    );

    if (botsNeedingReveal.length === 0) return;
    if (processingRef.current) return;

    processingRef.current = true;

    const revealBots = async () => {
      for (const botId of botsNeedingReveal) {
        await new Promise((r) => setTimeout(r, 800));
        const hand = players[botId]?.hand;
        if (!hand) continue;
        const handArray = Object.values(hand);
        const positions = chooseRevealPositions(handArray);
        try {
          await callFn('revealInitialCards', { playerId: botId, positions });
        } catch (err) {
          console.error('Bot reveal error:', err);
        }
      }
      processingRef.current = false;
    };

    timeoutRef.current = setTimeout(revealBots, BOT_DELAY);
    return () => {
      clearTimeout(timeoutRef.current);
      processingRef.current = false;
    };
  }, [gameState.phase, roundState?.revealedPlayers?.length, isHost, roomCode, playerOrder, players, callFn]);

  // Handle bot turns during PLAYING phase
  useEffect(() => {
    if (!isHost || !roomCode) return;
    if (gameState.phase !== PHASES.PLAYING && gameState.phase !== PHASES.ROUND_END_PHASE) return;
    if (!currentTurn) return;

    const botId = currentTurn.playerId;
    const bot = players[botId];
    if (!bot?.isBot) return;
    if (processingRef.current) return;

    processingRef.current = true;

    const playBotTurn = async () => {
      try {
        const hand = Object.values(bot.hand);

        if (currentTurn.phase === TURN_PHASES.WAITING_DRAW) {
          // Decide draw source
          const source = chooseDrawSource(hand, discardPile.topCard);
          const drawResult = await callFn('performDraw', { playerId: botId, source });

          // Small delay before decision
          await new Promise((r) => setTimeout(r, BOT_DELAY));

          // Now decide swap or discard
          const drawnCard = drawResult.card;
          const position = chooseSwapOrDiscard(hand, drawnCard);

          if (position >= 0) {
            await callFn('performSwap', { playerId: botId, handPosition: position });
          } else {
            await callFn('performDiscard', { playerId: botId });
          }
        }
      } catch (err) {
        console.error('Bot turn error:', err);
      } finally {
        processingRef.current = false;
      }
    };

    timeoutRef.current = setTimeout(playBotTurn, BOT_DELAY);
    return () => {
      clearTimeout(timeoutRef.current);
      processingRef.current = false;
    };
  }, [
    gameState.phase,
    currentTurn?.playerId,
    currentTurn?.phase,
    currentTurn?.turnNumber,
    isHost,
    roomCode,
    players,
    discardPile,
    callFn,
  ]);

  // Cleanup
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);
}

export { useBotPlayer };
